import type { ComputedRef } from 'vue'

import type { EntryPoint } from './useShoppingAssistantContext'
import type { NewChatDialog } from './useShoppingAssistantHost.injection-key'

type OpenOrRestart = (options: {
  entryPoint: EntryPoint
  entrySource?: string | null
  message: string
}) => void

/**
 * Orchestrates a seeded launch from a URL hash for the web sidebar: opens a
 * brand-new conversation pre-filled with the message. If a real conversation
 * is already open, it confirms first instead of silently discarding it —
 * mirroring the search bar's "search with AI" confirm-then-restart flow
 * (see SearchBar.vue) rather than the web-page host's force-new behavior,
 * since here the seed is arriving passively (route change), not from a
 * direct user click expressing clear intent to replace the chat.
 *
 * The hash itself is consumed by the caller (see useShoppingAssistantHashContext
 * + the route.hash watcher in WebSidebarShoppingAssistantHost.vue) regardless
 * of outcome, so this is strictly one-shot per hash value.
 *
 * `isOpen` here only decides open() vs. restart()-through-newChat() (i.e.
 * whether the panel needs to become visible) — the confirm-vs-skip decision
 * itself is delegated to `newChat`, which gates on whether the live
 * conversation actually has content (store.hasUserMessages), not on `isOpen`.
 * SearchBar.vue's own "search with AI" flow makes the same open-vs-confirm
 * decision independently (it can't share this host instance — see
 * useWebSidebarShoppingAssistantHost.ts), but reads the same
 * store.hasUserMessages flag for the confirm-vs-skip half of it, so the two
 * flows can't diverge on what counts as "something to lose".
 */
export function useSeededSidebarLaunch({
  isSeededLaunch,
  seededMessage,
  entrySource,
  isOpen,
  open,
  restart,
  newChat,
  dialog,
  trackSeededOpen,
}: {
  isSeededLaunch: ComputedRef<boolean>
  seededMessage: ComputedRef<string>
  entrySource: ComputedRef<string>
  isOpen: ComputedRef<boolean>
  open: OpenOrRestart
  restart: OpenOrRestart
  newChat: (dialog: NewChatDialog) => void
  dialog: Omit<NewChatDialog, 'onAccept' | 'onDecline'>
  trackSeededOpen: () => void
}) {
  function maybeLaunch(): boolean {
    if (!isSeededLaunch.value) {
      return false
    }

    const message = seededMessage.value
    const source = entrySource.value || null

    if (isOpen.value) {
      newChat({
        ...dialog,
        onAccept: () => {
          restart({ entryPoint: 'seeded_link', entrySource: source, message })
          trackSeededOpen()
        },
      })

      return true
    }

    open({ entryPoint: 'seeded_link', entrySource: source, message })
    trackSeededOpen()

    return true
  }

  return {
    maybeLaunch,
  }
}
